import styled from "styled-components";

import { Result } from "antd";
import { PageHeader } from "components/pageHeader";
import { AiDialerTemplatesTable } from "features/aiDialerTemplates";
import AppearanceManagementTable from "features/appearance/components/appearancesTable";
import { useFetchMeQuery } from "features/auth/authAPI";
import { AuthenticationMethodsTable } from "features/authenticationMethods";
import BackupDates from "features/backupDates/backupDates";
import { useFetchAgencyQuery } from "features/basicInfo/agencyPortal/agencySettingsAPI";
import BasicInfo from "features/basicInfo/basicInfo";
import { CreditReporting } from "features/creditReporting";
import { EmailTemplatesTable } from "features/emailTemplates";
import { FeePlans } from "features/feePlans";
import { LetterTemplates } from "features/letterTemplates";
import { MergeGroups } from "features/mergeGroups";
import { OperatingStatesTable } from "features/operatingStates";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import ProductOfferingTable from "features/productOfferings/productOfferingsTable";
import { SettingsTabs } from "features/settingsTabs";
import { SettlementAndPaymentRules } from "features/settlementAndPaymentRules";
import { CreditorTagTable, DebtorTagTable, DisputeCategoryTagTable } from "features/tags";
import { TaxPlans } from "features/taxPlans";
import { UserDefinedFieldsTable } from "features/userDefinedFields";
import PortalConfigurations from "features/portalConfigurations/components/portalConfigurations";
import Content from "layouts/agencyPortal/dashboard/content";
import PageLayout from "layouts/agencyPortal/dashboard/pageLayout";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { DebtorAssetCategoriesTable } from "features/debtorAssetCategories";
import { DesksTable } from "features/desksTable";
import PacketingRulesTable from "features/packetingRulesTable/packetingRulesTable";
import { CommunicationResultItemsTable } from "features/communicationResultItems";

const StyledContainer = styled.div`
  margin-left: 256px;
  padding: 8px 24px;
`;
const getItem = (label, key, icon, children, type) => {
  return {
    key,
    // icon,
    children,
    label,
    type,
  };
};

function SettingsPage() {
  const { data: agency } = useFetchAgencyQuery();
  const { data: me } = useFetchMeQuery();
  const {
    data: {
      [PERMISSIONS.SETTLEMENT_PAYMENT_RULE__GET]: isSettlementPaymentRulesAuthorized,
      [PERMISSIONS.CREDIT_REPORT_SETTINGS__GET]: isSettingsCreditReportingAuthorized,
      [PERMISSIONS.LETTER_TEMPLATE__GET]: isSettingsLetterTemplatesAuthorized,
      [PERMISSIONS.DEBTOR_TAG__GET]: isDebtorTagAuthorized,
      [PERMISSIONS.CREDITOR_TAG__GET]: isCreditorTagAuthorized,
      [PERMISSIONS.DISPUTE_CATEGORY_TAG__GET]: isDisputeCategoryTagAuthorized,
      [PERMISSIONS.FEE_PLAN_RULE__GET]: isFeePlanRuleAuthorized,
      [PERMISSIONS.PACKETING_RULE__GET]: isPacketingRulesAuthorized,
      [PERMISSIONS.LEGAL_FIELD__GET]: isLegalFieldAuthorized,
      [PERMISSIONS.DEBTOR_USER_DEFINED_FIELD__GET]: isDebtorUserDefinedFieldAuthorized,
      [PERMISSIONS.ACCOUNT_USER_DEFINED_FIELD__GET]: isAccountUserDefinedFieldAuthorized,
      [PERMISSIONS.CREDITOR_USER_DEFINED_FIELD__GET]: isCreditorUserDefinedFieldAuthorized,
      [PERMISSIONS.USER_USER_DEFINED_FIELD__GET]: isUserUserDefinedFieldAuthorized,
      [PERMISSIONS.PRODUCT_OFFERING__GET]: isProductsAuthorized,
      [PERMISSIONS.TAX_PLAN__GET]: isTaxPlanAuthorized,
      [PERMISSIONS.APPEARANCE__UPDATE]: isAppearanceUpdateAuthorized,
      [PERMISSIONS.PORTAL_CONFIGURATION__GET]: isPortalConfigurationAuthorized,
      [PERMISSIONS.DESK__GET]: isDesksAuthorized,
      [PERMISSIONS.DEBTOR_ASSET_CATEGORY__GET]: isDebtorAssetCategoryAuthorized,
      [PERMISSIONS.DEBTOR_ASSET_USER_DEFINED_FIELD__GET]: isDebtorAssetUserDefinedFieldAuthorized,
      [PERMISSIONS.AGENCY__GET]: isAgencyGetAuthorized,
      [PERMISSIONS.AGENCY__UPDATE]: isAgencyUpdateAuthorized,
    },
    isLoading,
  } = useAuthorizations();

  const isAiDialerTemplatesAuthorized = me?.isStaff && agency?.isAiDialerEnabled;

  if (isLoading) {
    return null;
  }
  const isBasicInfoAuthorized = isAgencyGetAuthorized || isAgencyUpdateAuthorized;
  const items = [
    isBasicInfoAuthorized &&
      getItem(
        <Link to="/settings/basic-info">Basic Info</Link>,
        "basic-info",
        null,
        null,
        "subMenu",
      ),
    isAgencyUpdateAuthorized &&
      getItem(
        <Link to="/settings/operating-states">Operating States</Link>,
        "operating-states",
        null,
        null,
        "subMenu",
      ),
    getItem(
      <Link to="/settings/backup-dates">Backup Dates</Link>,
      "backup-dates",
      null,
      null,
      "subMenu",
    ),
    isFeePlanRuleAuthorized &&
      getItem(<Link to="/settings/fee-plans">Fee Plans</Link>, "fee-plans", null, null, "subMenu"),
    isPacketingRulesAuthorized &&
      getItem(
        <Link to="/settings/packeting-rules">Packeting Rules</Link>,
        "packeting-rules",
        null,
        null,
        "subMenu",
      ),
    isSettlementPaymentRulesAuthorized &&
      getItem(
        <Link to="/settings/payment-rules">Payment Rules</Link>,
        "payment-rules",
        null,
        null,
        "subMenu",
      ),
    isTaxPlanAuthorized &&
      getItem(<Link to="/settings/tax-plans">Tax Plans</Link>, "tax-plans", null, null, "subMenu"),
    getItem(
      <Link to="/settings/merge-groups">Merge Groups</Link>,
      "merge-groups",
      null,
      null,
      "subMenu",
    ),
    isDesksAuthorized &&
      getItem(<Link to="/settings/desks">Desks</Link>, "desks", null, null, "subMenu"),
    getItem("Communication Results", "communication-results", null, [
      getItem(<Link to="/settings/call-results">Call</Link>, "call-results", null, null, "subMenu"),
      getItem(<Link to="/settings/text-results">Text</Link>, "text-results", null, null, "subMenu"),
      getItem(
        <Link to="/settings/email-results">Email</Link>,
        "email-results",
        null,
        null,
        "subMenu",
      ),
      getItem(
        <Link to="/settings/voicemail-results">Voicemail</Link>,
        "voicemail-results",
        null,
        null,
        "subMenu",
      ),
      getItem(<Link to="/settings/mail-results">Mail</Link>, "mail-results", null, null, "subMenu"),
      getItem(
        <Link to="/settings/communication-results">Communication</Link>,
        "general-results",
        null,
        null,
        "subMenu",
      ),
    ]),
    isDebtorAssetCategoryAuthorized &&
      getItem(
        <Link to="/settings/debtor-asset-categories/">Debtor Asset Categories</Link>,
        "debtor-asset-categories/",
        null,
        null,
        "subMenu",
      ),
    (isDebtorUserDefinedFieldAuthorized ||
      isAccountUserDefinedFieldAuthorized ||
      isCreditorUserDefinedFieldAuthorized ||
      isUserUserDefinedFieldAuthorized ||
      isLegalFieldAuthorized ||
      isDebtorAssetUserDefinedFieldAuthorized) &&
      getItem("User Defined Fields", "udf", null, [
        isDebtorUserDefinedFieldAuthorized &&
          getItem(
            <Link to="/settings/debtor-udf">Debtor</Link>,
            "debtor-udf",
            null,
            null,
            "subMenu",
          ),
        isAccountUserDefinedFieldAuthorized &&
          getItem(
            <Link to="/settings/account-udf">Account</Link>,
            "account-udf",
            null,
            null,
            "subMenu",
          ),
        isCreditorUserDefinedFieldAuthorized &&
          getItem(
            <Link to="/settings/creditor-udf">Client</Link>,
            "creditor-udf",
            null,
            null,
            "subMenu",
          ),
        isUserUserDefinedFieldAuthorized &&
          getItem(<Link to="/settings/user-udf">User</Link>, "user-udf", null, null, "subMenu"),
        isLegalFieldAuthorized &&
          getItem(<Link to="/settings/legal-udf">Legal</Link>, "legal-udf", null, null, "subMenu"),
        isDebtorAssetUserDefinedFieldAuthorized &&
          getItem(
            <Link to="/settings/debtor-asset-udf">Debtor Asset</Link>,
            "debtor-asset-udf",
            null,
            null,
            "subMenu",
          ),
      ]),
    (isSettingsLetterTemplatesAuthorized ||
      agency?.isEmailEnabled ||
      isAiDialerTemplatesAuthorized) &&
      getItem("Templates", "templates", null, [
        isSettingsLetterTemplatesAuthorized &&
          getItem(
            <Link to="/settings/letter-templates">Letter</Link>,
            "letter",
            null,
            null,
            "subMenu",
          ),
        agency?.isEmailEnabled &&
          getItem(
            <Link to="/settings/email-templates">Email</Link>,
            "email",
            null,
            null,
            "subMenu",
          ),
        isAiDialerTemplatesAuthorized &&
          getItem(
            <Link to="/settings/ai-dialer-templates">AI Phone Agent</Link>,
            "ai-dialer",
            null,
            null,
            "subMenu",
          ),
      ]),
    isSettingsCreditReportingAuthorized &&
      getItem(
        <Link to="/settings/credit-reporting">Credit Reporting</Link>,
        "credit-reporting",
        null,
        null,
        "subMenu",
      ),
    isProductsAuthorized &&
      getItem(<Link to="/settings/products">Products</Link>, "products", null, null, "subMenu"),
    (isDebtorTagAuthorized || isCreditorTagAuthorized || isDisputeCategoryTagAuthorized) &&
      getItem("Tags", "tags", null, [
        isDebtorTagAuthorized &&
          getItem(
            <Link to="/settings/creditor-tags">Client</Link>,
            "creditor-tags",
            null,
            null,
            "subMenu",
          ),
        isCreditorTagAuthorized &&
          getItem(
            <Link to="/settings/debtor-tags">Debtor</Link>,
            "debtor-tags",
            null,
            null,
            "subMenu",
          ),
        isDisputeCategoryTagAuthorized &&
          getItem(
            <Link to="/settings/dispute-category-tags">Dispute Category</Link>,
            "dispute-category-tags",
            null,
            null,
            "subMenu",
          ),
      ]),
    getItem(
      <Link to="/settings/authentication-methods">Authentication Methods</Link>,
      "authentication-methods",
      null,
      null,
      "subMenu",
    ),
    isPortalConfigurationAuthorized &&
      getItem(
        <Link to="/settings/portal-configurations">Portal Configurations</Link>,
        "portal-configurations",
        null,
        null,
        "subMenu",
      ),
    isAppearanceUpdateAuthorized &&
      getItem(
        <Link to="/settings/appearances">Appearances</Link>,
        "appearances",
        null,
        null,
        "subMenu",
      ),
  ].filter((item) => !!item); // Filter array of items that may contain 'false' when user doesn't have appropriate permissions

  if (items.length === 0) {
    return (
      <PageLayout>
        <Content>
          <PageHeader>Settings</PageHeader>
          <Result
            status="403"
            title="Oops!"
            subTitle="You do not have access to any settings"
            extra={<Link to="/accounts">Back Home</Link>}
          />
        </Content>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Content>
        <SettingsTabs items={items} />
        <StyledContainer>
          <Routes>
            <Route path="/" element={<Navigate to={`/settings/${items[0]?.key}`} replace />} />
            <Route path="/basic-info" element={<BasicInfo />} />
            <Route path="/operating-states" element={<OperatingStatesTable />} />
            <Route path="/backup-dates" element={<BackupDates />} />
            <Route path="/fee-plans" element={<FeePlans />} />
            <Route path="/packeting-rules" element={<PacketingRulesTable />} />
            <Route path="/payment-rules" element={<SettlementAndPaymentRules />} />
            <Route path="/tax-plans" element={<TaxPlans />} />
            <Route path="/merge-groups" element={<MergeGroups />} />
            <Route path="/desks" element={<DesksTable />} />
            <Route
              path="/call-results"
              element={<CommunicationResultItemsTable resultType="call" />}
            />
            <Route
              path="/text-results"
              element={<CommunicationResultItemsTable resultType="text" />}
            />
            <Route
              path="/email-results"
              element={<CommunicationResultItemsTable resultType="email" />}
            />
            <Route
              path="/voicemail-results"
              element={<CommunicationResultItemsTable resultType="voicemail" />}
            />
            <Route
              path="/mail-results"
              element={<CommunicationResultItemsTable resultType="mail" />}
            />
            <Route
              path="/communication-results"
              element={<CommunicationResultItemsTable resultType="communication" />}
            />
            <Route path="/credit-reporting" element={<CreditReporting />} />
            <Route path="/debtor-udf" element={<UserDefinedFieldsTable forType="debtor" />} />
            <Route path="/account-udf" element={<UserDefinedFieldsTable forType="account" />} />
            <Route path="/creditor-udf" element={<UserDefinedFieldsTable forType="client" />} />
            <Route path="/user-udf" element={<UserDefinedFieldsTable forType="user" />} />
            <Route path="/legal-udf" element={<UserDefinedFieldsTable forType="legal" />} />
            <Route
              path="/debtor-asset-udf"
              element={<UserDefinedFieldsTable forType="debtorAsset" />}
            />
            <Route path="/debtor-asset-categories/" element={<DebtorAssetCategoriesTable />} />
            <Route path="/letter-templates" element={<LetterTemplates />} />
            <Route path="/email-templates" element={<EmailTemplatesTable />} />
            <Route path="/ai-dialer-templates" element={<AiDialerTemplatesTable />} />
            <Route path="/debtor-tags" element={<DebtorTagTable />} />
            <Route path="/creditor-tags" element={<CreditorTagTable />} />
            <Route path="/dispute-category-tags" element={<DisputeCategoryTagTable />} />
            <Route path="/products" element={<ProductOfferingTable />} />
            <Route path="/authentication-methods" element={<AuthenticationMethodsTable />} />
            <Route path="/portal-configurations" element={<PortalConfigurations />} />
            <Route path="/appearances" element={<AppearanceManagementTable />} />
          </Routes>
        </StyledContainer>
      </Content>
    </PageLayout>
  );
}

export default SettingsPage;
